<script setup>
import { computed, watch } from "vue";
import { useGlobalStore } from "@/stores/global";
import { useShopifyStore } from "@/stores/shopify";
import { useRoute } from "vue-router";
import Product from "@/components/cards/Product.vue";
import Button from "@/components/global/Button.vue";
import ArrowLeft from "@/components/icons/ArrowLeft.vue";

const route = useRoute();
const shopify = useShopifyStore();
const global = useGlobalStore();

const collection = computed(
	() => shopify.collections.find((c) => c.slug === route.params.slug) ?? null,
);

const products = computed(() => {
	if (!collection.value?.products) return [];
	return collection.value.products;
});

const gridItems = computed(() => {
	const items = [];
	for (const product of products.value) {
		items.push({ type: "product", product });
		if (product.collection_image) {
			items.push({
				type: "image",
				url: product.collection_image,
				alt: product.collection_image_alt,
				link: product.link_collection_image,
				product,
			});
		}
	}
	return items;
});

watch(
	collection,
	(collection) => {
		if (collection) {
			global.setTheme(
				collection.theme?.background,
				collection.theme?.accent,
				collection.theme?.surface,
			);
		}
	},
	{ immediate: true },
);
</script>

<template>
	<section
		v-if="collection?.banner"
		class="relative isolate flex min-h-[calc(80svh-var(--header-height)-var(--announcement-bar-height))] items-end justify-center pb-48 text-white before:absolute before:inset-0 before:-z-1 before:size-full before:bg-linear-to-t before:from-black/50 before:to-transparent"
	>
		<Button :to="{ name: 'teams' }" class="absolute top-16 left-16 z-1">
			<ArrowLeft />
			<p class="h8 font-primary-condensed">Back</p>
		</Button>
		<img
			:src="collection.banner"
			:alt="collection.banner_alt || collection.title"
			class="absolute inset-0 -z-2 size-full object-cover"
		/>
		<div class="flex flex-col gap-32">
			<img
				:src="collection.hero_logo"
				:alt="collection.hero_logo_alt || collection.title"
				class="h-48 w-auto object-contain lg:h-80"
				v-if="collection.hero_logo"
			/>
			<div class="flex flex-col items-center gap-24 text-center">
				<h1 class="h1 font-primary-condensed" v-if="collection.hero_title">
					{{ collection.hero_title }}
				</h1>
				<div class="body-md" v-if="collection.description">
					{{ collection.description }}
				</div>
			</div>
		</div>
	</section>
	<section class="py-48">
		<div class="container">
			<div class="grid grid-cols-2 gap-8 lg:grid-cols-3">
				<template v-for="(item, index) in gridItems" :key="index">
					<Product
						v-if="item.type === 'product'"
						:product="item.product"
					/>

					<router-link
						v-else-if="item.type === 'image' && item.link"
						:to="{ name: 'product', params: { slug: item.product.slug } }"
						class="block aspect-square overflow-hidden rounded-lg"
					>
						<img
							:src="item.url"
							:alt="item.alt || item.product.title"
							class="size-full object-cover"
						/>
					</router-link>

					<div
						v-else-if="item.type === 'image'"
						class="aspect-square overflow-hidden rounded-lg"
					>
						<img
							:src="item.url"
							:alt="item.alt || item.product.title"
							class="size-full object-cover"
						/>
					</div>
				</template>
			</div>
		</div>
	</section>
</template>
